import { Dialog } from '@headlessui/react'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'

import WarningIcon from '../../assets/images/icons/alert-triangle.svg?react'
import SuccessIcon from '../../assets/images/icons/success-icon.svg?react'
import XIcon from '../../assets/images/icons/x-close-filter.svg?react'
import { baseApi } from '../../store/api'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { closeExportReadyModal } from '../../store/slices/settingsSlice'

const exportResultMessages = (
  status: -1 | 1,
  reportId: string,
  action: () => void,
): {
  title: React.ReactNode
  Message: React.FC
  icon: React.ReactNode
  buttonLabel?: React.ReactNode
  buttonClass?: string
  buttonAction?: () => void
} =>
  ({
    [1]: {
      title: 'Export is ready!',
      Message: (): JSX.Element => (
        <>
          Your export is prepared and ready to download in Export Reports
          dropdown.
        </>
      ),
      icon: <SuccessIcon />,
      buttonLabel: 'Download Export',
      buttonClass: 'bg-primary-600 border-primary-600 text-white',
      buttonAction: action,
    },
    [-1]: {
      title: 'Export Failed',
      Message: (): JSX.Element => {
        const { show: showIntercom } = useIntercom()

        return (
          <>
            There was an error while preparing your export file. <br />
            Please try again, or{' '}
            <button
              className="text-primary-600 underline"
              onClick={() => showIntercom()}
            >
              contact support
            </button>{' '}
            if the issue continues.
            <span className="mt-5 text-xs block">
              <i>
                You may report this issue with id: <b>{reportId}</b>
              </i>
            </span>
          </>
        )
      },
      icon: <WarningIcon />,
    },
  })[status]

export default function ExportReadyModal() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const isExportOngoing = useAppSelector(
    (state) => state.settings.is_export_ongoing,
  )
  const exportStatus = useAppSelector(
    (state) => state.settings.latest_export_result_code,
  )
  const exportReportId = useAppSelector(
    (state) => state.settings.latest_export_report_id,
  )

  const isModalOpen = useAppSelector(
    (state) => state.settings.show_export_ready_modal,
  )

  useEffect(() => {
    if (!isExportOngoing) {
      dispatch(baseApi.util.invalidateTags(['ExportCount', 'Export']))
    }
  }, [isExportOngoing])

  const close = () => {
    dispatch(closeExportReadyModal())
  }

  const exportResult = exportResultMessages(
    exportStatus,
    exportReportId,
    exportStatus === -1
      ? () => {
          close()
        }
      : () => {
          close()
          navigate('/reports/export')
        },
  )

  return (
    <Dialog
      open={isModalOpen}
      onClose={() => close()}
      className="relative z-50"
    >
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          background: 'rgba(41, 41, 41, 0.7)',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Dialog.Panel className="relative border rounded-xl shadow-xl bg-white-100 sm:w-3/4 md:w-2/3 xl:w-96 w-full">
          <div className="flex flex-col p-6 w-full">
            <div className="flex items-center mb-5 relative">
              {exportResult.icon}{' '}
              <h1 className="text-center ml-3">{exportResult.title}</h1>
              <button className="absolute right-0" onClick={() => close()}>
                <XIcon />
              </button>
            </div>
            <div className="flex flex-col flex-wrap text-center">
              <p className="font-normal text-sm text-gray-500">
                <exportResult.Message />
              </p>
            </div>
            {exportResult?.buttonLabel ? (
              <button
                type="button"
                className={`w-full h-11 mt-6 flex justify-center items-center border border-solid shadow-sm rounded-lg ${exportResult.buttonClass}`}
                onClick={exportResult.buttonAction}
              >
                <span className="font-medium text-base text-inherit">
                  {exportResult.buttonLabel}
                </span>
              </button>
            ) : (
              <></>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}
